import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type Maybe from 'true-myth/maybe';

import type { JobProfileForGenerationWithPrompt } from '~/shared/data-contract/job-profile';

import type { RegenerationCommentsContext } from '../../data-contract/regeneration-comments-context';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import type { SkillsDetectionResult } from '../../legacy/position/api/redetect-skills-request';
import { jobArchitectureKeys } from './query-keys';

export type UseRedetectSkillsResult = UseMutationResult<
    SkillsDetectionResult,
    Error,
    {
        readonly additionalPrompt: Maybe<string>;
        readonly architectureId: string;
        readonly detectionType: 'adjust' | 'redetect';
        readonly jobProfile: JobProfileForGenerationWithPrompt;
        readonly jobProfileId: string;
        readonly commentsContext?: RegenerationCommentsContext;
    }
>;

export const useRedetectSkills = (): UseRedetectSkillsResult => {
    const { redetectSkills } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (variables) => {
            const result = await redetectSkills(variables);
            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onSuccess: (_, { architectureId, jobProfileId }) => {
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.profile(architectureId, jobProfileId),
            });
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.profiles(architectureId),
            });
        },
    });
};
